import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import { ErrorBoundary } from './components/errors/ErrorBoundary.jsx';
import './index.css';

const LEGACY_APP_SW_PATHS = ['/sw.js', '/service-worker.js'];
const LEGACY_APP_CACHE_PREFIXES = [
  'workbox-precache',
  'workbox-runtime',
  'workbox-googleAnalytics',
  'google-fonts',
  'supabase-api',
  'receipt-images',
  'app-html',
  'vite-pwa',
];

const getServiceWorkerScriptUrl = (registration) => registration.active?.scriptURL
  || registration.waiting?.scriptURL
  || registration.installing?.scriptURL
  || '';

const isLegacyAppWorker = (registration) => {
  const scriptUrl = getServiceWorkerScriptUrl(registration);
  return LEGACY_APP_SW_PATHS.some((path) => scriptUrl.includes(path));
};

const clearLegacyAppCaches = async () => {
  if (typeof caches === 'undefined') return;

  const cacheNames = await caches.keys();
  const appCacheNames = cacheNames.filter((name) => LEGACY_APP_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix)));
  await Promise.allSettled(appCacheNames.map((name) => caches.delete(name)));
};

// Cleanup legacy app service workers and their caches so published browsers
// stop serving stale JoinScreen bundles.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    Promise.allSettled([
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.allSettled(
          registrations
            .filter(isLegacyAppWorker)
            .map((registration) => registration.unregister())
        )),
      clearLegacyAppCaches(),
    ])
      .then(() => console.debug('[SW] stale app workers and caches cleared'))
      .catch((err) => console.warn('[SW] cleanup failed:', err));
  });
}

// Global error handler for uncaught promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('[Unhandled rejection]', event.reason);
  if (import.meta.env.PROD) {
    // reportErrorToSentry(event.reason);
  }
});

// Measure app init time
if (typeof performance !== 'undefined') {
  performance.mark('app-init-start');
  window.addEventListener('load', () => {
    performance.mark('app-init-end');
    performance.measure('app-init', 'app-init-start', 'app-init-end');
    const [entry] = performance.getEntriesByName('app-init', 'measure');
    if (entry && import.meta.env.DEV) {
      console.debug(`[Perf] App init: ${entry.duration.toFixed(0)}ms`);
    }
  });
}

const root = document.getElementById('root');

if (!root) {
  throw new Error('[SplitSnap] Root element #root not found in DOM');
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
